import React, { useState } from "react";
import axios from "axios";
import "../../wallet.css";

const ISSUE_TYPES = [
  "Booking Not Found",
  "Wrong Address / Location",
  "Scan Failed",
  "Status Update Failed",
  "Payment / COD Issue",
  "App or Page Error",
  "Other",
];

/**
 * Floating "Report Issue" button + modal, dropped into any ops page (InScan, OutScan,
 * BulkScan, Update Status, Bulk Tracking, ...) so a supervisor can file a support ticket
 * without leaving what they're doing. Self-contained — imports its own CSS and does its own
 * submit, so pages only need one import + one JSX line.
 *
 * Tickets land in the same {@code support_tickets} queue as the driver app's auto crash
 * reports; IT works both from the Support Tickets admin page.
 */
export default function ReportIssueButton({ pageContext, pageLabel, awb: awbProp }) {
  const API_URL = process.env.REACT_APP_API_URL;
  const TICKETS_API = `${API_URL}shypvdriverapp/support-tickets`;

  const [open, setOpen] = useState(false);
  const [issueType, setIssueType] = useState(ISSUE_TYPES[0]);
  const [awb, setAwb] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { type: 'success'|'error', text }

  const reporterName = sessionStorage.getItem("user_name") || sessionStorage.getItem("username") || "";

  const openModal = () => {
    setAwb(awbProp || "");
    setDescription("");
    setIssueType(ISSUE_TYPES[0]);
    setResult(null);
    setOpen(true);
  };

  const submit = async () => {
    if (!description.trim()) {
      setResult({ type: "error", text: "Please describe the issue." });
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const res = await axios.post(TICKETS_API, {
        reporterType: "SUPERVISOR",
        reporterName,
        awb: awb || null,
        pageContext,
        issueType,
        context: description.trim(),
      });
      if (res.data?.success !== false) {
        setResult({ type: "success", text: "Reported — IT will follow up." });
        setTimeout(() => setOpen(false), 1200);
      } else {
        setResult({ type: "error", text: res.data?.message || "Failed to report issue." });
      }
    } catch (err) {
      setResult({ type: "error", text: err.response?.data?.message || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={openModal}
        title="Report an issue on this page"
        style={{
          position: "fixed",
          right: 20,
          bottom: 20,
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 16px",
          borderRadius: 999,
          border: "none",
          background: "#dc2626",
          color: "#fff",
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          boxShadow: "0 6px 16px rgba(220,38,38,.35)",
        }}
      >
        <i className="fa fa-exclamation-triangle" aria-hidden="true" />
        Report Issue
      </button>

      {open && (
        <div className="modal-overlay w-modal">
          <div className="modal-content" style={{ maxWidth: 480 }}>
            <div className="w-modal-header">
              <div className="w-modal-icon" style={{ background: "#fee2e2", color: "#dc2626" }}>
                <i className="fa fa-exclamation-triangle" aria-hidden="true" />
              </div>
              <div style={{ flex: 1 }}>
                <h3>Report an issue</h3>
                <p>{pageLabel ? `From ${pageLabel}` : "IT will follow up on this."}</p>
              </div>
              <button className="close-btn" onClick={() => setOpen(false)}>
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>

            <div className="modal-body">
              {result && (
                <div className={`w-banner ${result.type === "success" ? "w-banner-success" : "w-banner-error"}`} style={{ marginBottom: 12 }}>
                  {result.text}
                </div>
              )}

              <div className="w-field" style={{ marginBottom: 12 }}>
                <label className="w-field-label">Issue type</label>
                <select className="w-input" value={issueType} onChange={(e) => setIssueType(e.target.value)}>
                  {ISSUE_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="w-field" style={{ marginBottom: 12 }}>
                <label className="w-field-label">AWB / reference (optional)</label>
                <input className="w-input" value={awb} onChange={(e) => setAwb(e.target.value)} placeholder="e.g. UB07-260701-0001" />
              </div>

              <div className="w-field">
                <label className="w-field-label">
                  Describe the issue <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <textarea
                  className="w-textarea"
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What happened?"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="w-btn w-btn-ghost" onClick={() => setOpen(false)} disabled={submitting}>
                Cancel
              </button>
              <button className="w-btn w-btn-primary" onClick={submit} disabled={submitting}>
                {submitting ? "Sending…" : "Submit report"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
